import { ComponentType } from 'react';

import { session, SESSION_KEYS } from './storage';

/**
 * A lazy chunk that fails to load is almost always a stale index.html pointing at hashed files a
 * newer deploy has replaced. One reload fetches the current index and fixes it. A second failure
 * inside this window means the reload did not help (the index is still cached, or the network is
 * down), so the error surfaces to the route's error boundary instead of reloading forever.
 */
export const LAZY_RELOAD_COOLDOWN_MS = 60_000;

const lastReloadAt = (): number => {
  const stored = Number(session.get(SESSION_KEYS.RETRY_LAZY_REFRESHED) ?? 0);

  return Number.isFinite(stored) ? stored : 0;
};

/**
 * Retries a lazy-loaded component import once after a hard refresh to recover from transient load failures.
 * @param componentImport Function that dynamically imports a component with a default export.
 * @returns Promise resolving to the component module compatible with React.lazy.
 */
export const lazyRetry = <P>(
  componentImport: () => Promise<{ default: ComponentType<P> }>
): Promise<{ default: ComponentType<P> }> =>
  componentImport().catch((error: Error) => {
    const now = Date.now();

    if (now - lastReloadAt() < LAZY_RELOAD_COOLDOWN_MS) {
      throw error; // already reloaded for this a moment ago; let the error boundary show it
    }

    session.set(SESSION_KEYS.RETRY_LAZY_REFRESHED, String(now));
    window.location.reload();

    // The page is being replaced; keep React.lazy waiting instead of rendering an error first.
    return new Promise<never>(() => {});
  });
